// LOI comparison on a seller deal (backend app/loi_compare.py).
//
// The 11-02 huddle compared Hydra Tech's LOIs from memory. One column per
// buyer on the deal that submitted an LOI, one row per term; red flags on
// top. Every value opens what it came from: the clause the letter says it
// in and the page (AI verdicts show their work), the buyer board's own
// field, a person's edit, or the sum that made it. A blank stays blank.
//
//   window.LoiCompareCard        the deal page's card (classic center + v2 rail)
//   window.LoiRedFlagsSettings   Settings → LOI red flags: the one list of
//                                non-standard terms every letter is checked for
//
// Seller deals only: gated on deal.type !== 'buyer_buyside' (a buyer shell
// sits in seller stages, so never stageCategory), fetch included.

const LOI_POLL_MS = 3000;
const LOI_READING = 'running';

const loiMoney = (v) => (v == null ? null : window.fmtMoney(v));
const loiDate = (iso) => (iso ? window.fmtDate(iso) : null);

// The cell as one short line: '$19.5M', '$2.0M · 3 years', '60 days'.
const loiCellText = (term, cell) => {
  if (!cell) return null;
  if (term.kind === 'date') return loiDate(cell.text);
  if (term.kind === 'money') {
    const m = loiMoney(cell.value);
    if (term.key === 'earnout') return [m, cell.period].filter(Boolean).join(' · ') || cell.text || null;
    return m || cell.text || null;
  }
  return cell.text || null;
};

// Best offer first: headline (or implied value) high → low, then by date.
const loiSortColumns = (cols) => cols.slice().sort((a, b) => {
  const v = (c) => ((c.cells.headline_price || {}).value ?? (c.cells.implied_no_earnout || {}).value ?? -1);
  return v(b) - v(a) || String(a.buyer_name).localeCompare(String(b.buyer_name));
});

// The highest figure on a money row (more for the seller), when two or more columns have one.
const LOI_NO_BEST = new Set(['seller_note', 'earnout', 'rollover', 'retention_bonus']);   // more isn't better for the seller
const loiBest = (cols, key) => {
  if (LOI_NO_BEST.has(key)) return null;
  const vals = cols.map((c) => (c.cells[key] || {}).value).filter((v) => typeof v === 'number');
  return vals.length >= 2 ? Math.max(...vals) : null;
};

const useLois = (dealId, enabled) => {
  const [state, setState] = React.useState({ data: null, error: null, loading: !!enabled });
  const load = React.useCallback(() => {
    if (!enabled || !dealId) return Promise.resolve();
    return window.AtlasApi.lois(dealId)
      .then((d) => setState({ data: d, error: null, loading: false }))
      .catch((e) => setState((s) => ({ ...s, error: (e && e.message) || 'Could not load LOIs', loading: false })));
  }, [dealId, enabled]);
  React.useEffect(() => { load(); }, [load]);
  // Reload when the buyer board changes (an LOI ticked there adds a column).
  React.useEffect(() => {
    if (!enabled) return undefined;
    const evt = (window.RECORD_SUMMARY && window.RECORD_SUMMARY.EVT_BUYERS) || 'atlas-buyers-changed';
    const on = (e) => { const d = (e && e.detail) || {}; if (!d.engagementId || d.engagementId === String(dealId)) load(); };
    window.addEventListener(evt, on);
    return () => window.removeEventListener(evt, on);
  }, [enabled, dealId, load]);
  const reading = !!(state.data && state.data.columns.some((c) => c.review && c.review.status === LOI_READING));
  React.useEffect(() => {
    if (!reading) return undefined;
    const t = setInterval(load, LOI_POLL_MS);
    return () => clearInterval(t);
  }, [reading, load]);
  return { ...state, reload: load, reading };
};

const LoiFlagMark = ({ n }) => (n > 0 ? (
  <span role="img" aria-label={`${n} red flag${n === 1 ? '' : 's'}`} title={`${n} red flag${n === 1 ? '' : 's'}`}
    style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--rose-fg)', fontSize: 11.5, fontWeight: 600 }}>
    <window.Icon.Flag size={11} />{n}
  </span>
) : null);

// ── the deal page card ───────────────────────────────────────────────────────
const LoiCompareCard = ({ deal, variant = 'card', sectionId = 'deal.lois' }) => {
  const seller = !!deal && deal.type !== 'buyer_buyside' && !window.AtlasIsGuest;
  const lois = useLois(deal && deal.id, seller);
  const [open, setOpen] = React.useState(false);
  if (!seller) return null;
  const d = lois.data;
  if (!d && !lois.error) return null;                       // loading: no card flashing in
  if (d && !d.columns.length && !d.roster_count) return null;  // no buyers yet: nothing to compare
  const cols = d ? loiSortColumns(d.columns) : [];
  const flags = cols.reduce((n, c) => n + c.red_flags.length, 0);
  const title = <span>LOIs · {d ? cols.length : '—'}</span>;
  const preview = cols.length
    ? cols.slice(0, 3).map((c) => `${c.buyer_name} ${loiMoney((c.cells.headline_price || {}).value) || ''}`.trim()).join(' · ')
      + (flags ? ` · ${flags} red flag${flags === 1 ? '' : 's'}` : '')
    : 'None yet';
  const action = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {lois.error && <Btn size="xs" variant="ghost" title="Retry" icon={<Icon.Refresh size={11} />} onClick={lois.reload} />}
      {d && <Btn size="xs" variant="ghost" title="Compare LOIs" icon={<Icon.Maximize size={11} />} onClick={() => setOpen(true)} />}
    </span>
  );
  return (
    <window.Section variant={variant === 'rail' ? 'rail' : 'card'} title={title} collapsible id={sectionId} preview={preview} action={action}>
      <div style={{ padding: variant === 'rail' ? '4px 14px 12px' : '8px 14px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {lois.error && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Couldn't load LOIs.</div>}
        {d && !cols.length && (
          <button type="button" onClick={() => setOpen(true)}
            style={{ background: 'none', border: 'none', padding: '2px 0', font: 'inherit', fontSize: 12, color: 'var(--text-3)', textAlign: 'left', cursor: 'pointer' }}>
            No LOIs yet · <span style={{ color: 'var(--accent-text)' }}>Add one</span>
          </button>
        )}
        {cols.map((c) => {
          const head = (c.cells.headline_price || {}).value;
          const st = c.review && c.review.status;
          return (
            <button key={c.buyer_engagement_id} type="button" onClick={() => setOpen(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, background: 'none', border: 'none', padding: '4px 0',
                       font: 'inherit', textAlign: 'left', cursor: 'pointer', color: 'var(--text)' }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.buyer_name}</span>
              {st === LOI_READING && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Reading…</span>}
              <LoiFlagMark n={c.red_flags.length} />
              <span className="num" style={{ fontSize: 12.5, fontWeight: 600, color: head == null ? 'var(--text-4)' : 'var(--text)' }}>{head == null ? '—' : loiMoney(head)}</span>
            </button>
          );
        })}
        {cols.length > 0 && (
          <div style={{ marginTop: 6 }}><Btn size="xs" variant="outline" onClick={() => setOpen(true)}>Compare</Btn></div>
        )}
      </div>
      {open && <LoiCompareDrawer deal={deal} lois={lois} onClose={() => setOpen(false)} />}
    </window.Section>
  );
};

// ── the comparison drawer ────────────────────────────────────────────────────
const LOI_LABEL_W = 168;
const LOI_COL_W = 188;

const LoiCompareDrawer = ({ deal, lois, onClose }) => {
  const d = lois.data || { columns: [], terms: [], rules: [] };
  const cols = loiSortColumns(d.columns);
  const canWrite = window.recordCanWrite ? window.recordCanWrite() : true;
  const [sel, setSel] = React.useState(null);          // { be, key } | { be, flag: i } | { be, trail: true }
  const [picking, setPicking] = React.useState(null);  // a column whose letter is being picked
  const [exporting, setExporting] = React.useState(false);
  const [roster, setRoster] = React.useState(null);
  const col = sel ? cols.find((c) => c.buyer_engagement_id === sel.be) : null;
  const phone = !!(window.useDevice && window.useDevice().phone);

  React.useEffect(() => {
    if (!canWrite) return undefined;
    let alive = true;
    window.AtlasApi.buyersForEngagement(deal.id).then((r) => { if (alive) setRoster((r && r.items) || []); }).catch(() => { if (alive) setRoster([]); });
    return () => { alive = false; };
  }, [deal.id, canWrite, d.columns.length]);

  const inTable = new Set(cols.map((c) => c.buyer_engagement_id));
  const addable = (roster || []).filter((b) => !inTable.has(b.id) && b.interest_level !== 'passed');
  const addBuyer = (beId) => {
    if (!beId) return;
    window.AtlasApi.updateBuyerEngagement(beId, { loi_submitted: true })
      .then(() => { if (window.notifyBuyersChanged) window.notifyBuyersChanged(deal.id, 'loi-compare'); return lois.reload(); })
      .catch((e) => window.pushToast({ type: 'error', title: "Couldn't add the LOI", body: e.message }));
  };
  const doExport = () => {
    setExporting(true);
    window.AtlasApi.exportLois(deal.id)
      .catch((e) => window.pushToast({ type: 'error', title: 'Export failed', body: e.message }))
      .finally(() => setExporting(false));
  };
  const reread = (c) => window.AtlasApi.rereadLoi(deal.id, c.buyer_engagement_id).then(lois.reload)
    .catch((e) => window.pushToast({ type: 'error', title: "Couldn't read the letter", body: e.message }));

  const toolbar = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
      {canWrite && roster && addable.length > 0 && (
        <window.Picker value="" onChange={addBuyer} size="sm" minWidth={180} placeholder="+ Add LOI from…"
          options={addable.map((b) => ({ value: b.id, label: b.buyer_name, hint: b.interest_level || undefined }))} />
      )}
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
        {lois.reading && <span style={{ fontSize: 12, color: 'var(--text-3)', alignSelf: 'center' }}>Reading letters…</span>}
        <Btn size="sm" variant="outline" icon={<Icon.Download size={12} />} onClick={doExport} disabled={!cols.length || exporting}
          title="Download as Excel">{exporting ? 'Exporting…' : 'Export'}</Btn>
      </span>
    </div>
  );

  return (
    <window.Drawer open title="LOI comparison" subtitle={deal.name} width={1180} widthKey="loiCompare" onClose={onClose}
      onEscape={sel ? () => setSel(null) : onClose}>
      {toolbar}
      {!cols.length ? (
        <div style={{ padding: '28px 16px', fontSize: 13, color: 'var(--text-3)' }}>
          No LOIs on this deal yet{canWrite && addable.length ? ' — add the buyers that sent one.' : '.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: '999 1 520px', minWidth: 0, overflowX: 'auto' }}>
            <LoiMatrix cols={cols} terms={d.terms} sel={sel} onSelect={setSel} canWrite={canWrite}
              onPick={setPicking} onReread={reread} />
          </div>
          {sel && col && (
            <div style={{ flex: '1 1 320px', maxWidth: '100%', borderLeft: '1px solid var(--border)', alignSelf: 'stretch',
                          ...(phone ? { order: -1, borderLeft: 'none', borderBottom: '1px solid var(--border)' } : null) }}>
              <LoiEvidence deal={deal} col={col} sel={sel} terms={d.terms} canWrite={canWrite} onClose={() => setSel(null)}
                onSaved={lois.reload} />
            </div>
          )}
        </div>
      )}
      {picking && (
        <LoiLetterPicker deal={deal} col={picking} onClose={() => setPicking(null)}
          onPicked={() => { setPicking(null); lois.reload(); }} />
      )}
    </window.Drawer>
  );
};

const loiCellBtn = (selected) => ({
  display: 'block', width: '100%', minHeight: 34, padding: '7px 10px', textAlign: 'left', font: 'inherit', fontSize: 12.5,
  background: selected ? 'var(--accent-soft)' : 'transparent', border: 'none', borderRadius: 0, cursor: 'pointer', color: 'var(--text)',
});
const loiStickyLabel = (w) => ({
  position: 'sticky', left: 0, zIndex: 1, background: 'var(--surface)', width: w, minWidth: w,
  padding: '7px 12px', fontSize: 12, fontWeight: 500, color: 'var(--text-2)', textAlign: 'left', verticalAlign: 'top',
  borderRight: '1px solid var(--border)',
});
// A phone keeps the term column narrow so a buyer's column still fits beside it.
const loiLabelW = () => (window.useDevice && window.useDevice().phone ? 112 : LOI_LABEL_W);

const LoiMatrix = ({ cols, terms, sel, onSelect, canWrite, onPick, onReread }) => {
  const labelW = loiLabelW();
  const label = loiStickyLabel(labelW);
  const isSel = (be, key) => sel && sel.be === be && sel.key === key;
  const td = { borderBottom: '1px solid var(--border)', padding: 0, verticalAlign: 'top', width: LOI_COL_W, minWidth: LOI_COL_W };
  return (
    <table style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', width: labelW + cols.length * LOI_COL_W }}>
      <thead>
        <tr>
          <th style={{ ...label, borderBottom: '1px solid var(--border)', zIndex: 2 }} scope="col" aria-label="Term" />
          {cols.map((c) => <LoiColumnHead key={c.buyer_engagement_id} col={c} canWrite={canWrite} onPick={onPick} onReread={onReread}
            onTrail={() => onSelect({ be: c.buyer_engagement_id, trail: true })} />)}
        </tr>
      </thead>
      <tbody>
        <tr>
          <th scope="row" style={{ ...label, borderBottom: '1px solid var(--border)', color: 'var(--rose-fg)' }}>Red flags</th>
          {cols.map((c) => (
            <td key={c.buyer_engagement_id} style={{ ...td, padding: '6px 10px' }}>
              {c.red_flags.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                  {c.red_flags.map((f, i) => (
                    <button key={i} type="button" onClick={() => onSelect({ be: c.buyer_engagement_id, flag: i })}
                      aria-pressed={!!(sel && sel.be === c.buyer_engagement_id && sel.flag === i)}
                      style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 5, background: 'var(--rose-bg)', color: 'var(--rose-fg)',
                               border: 'none', borderRadius: 6, padding: '3px 7px', font: 'inherit', fontSize: 11.5, fontWeight: 500,
                               textAlign: 'left', cursor: 'pointer', maxWidth: '100%' }}>
                      <span style={{ flexShrink: 0, marginTop: 2 }}><window.Icon.Flag size={10} /></span>{f.label}
                    </button>
                  ))}
                </div>
              ) : (
                <span style={{ fontSize: 12, color: 'var(--text-4)' }}>{c.review && c.review.status === 'done' ? 'None' : '—'}</span>
              )}
            </td>
          ))}
        </tr>
        {terms.map((t) => {
          const best = t.kind === 'money' ? loiBest(cols, t.key) : null;
          return (
            <tr key={t.key}>
              <th scope="row" style={{ ...label, borderBottom: '1px solid var(--border)',
                                       fontWeight: t.key === 'headline_price' || t.key === 'implied_no_earnout' ? 600 : 500 }}>{t.label}</th>
              {cols.map((c) => {
                const cell = c.cells[t.key];
                const txt = loiCellText(t, cell);
                const top = best != null && cell && cell.value === best;
                return (
                  <td key={c.buyer_engagement_id} style={td}>
                    <button type="button" style={loiCellBtn(isSel(c.buyer_engagement_id, t.key))}
                      aria-pressed={isSel(c.buyer_engagement_id, t.key)}
                      aria-label={`${t.label}, ${c.buyer_name}: ${txt || 'blank'}`}
                      onClick={() => onSelect({ be: c.buyer_engagement_id, key: t.key })}>
                      <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
                        <span className={t.kind === 'money' ? 'num' : undefined} title={top ? 'Highest' : undefined}
                          style={{ flex: 1, minWidth: 0, fontWeight: top ? 700 : (t.kind === 'money' ? 500 : 400),
                                   color: txt ? 'var(--text)' : 'var(--text-4)', overflow: 'hidden', textOverflow: 'ellipsis',
                                   whiteSpace: t.kind === 'text' ? 'normal' : 'nowrap',
                                   display: t.kind === 'text' ? '-webkit-box' : undefined, WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                          {txt || '—'}{top ? <span aria-hidden style={{ color: 'var(--green-fg)', marginLeft: 4 }}>▲</span> : null}
                        </span>
                        <LoiSourceMark cell={cell} />
                      </span>
                    </button>
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// How a value got here, as a muted tag: p. 3 · letter · edited · = · board · DC · ≠ letter.
const LoiSourceMark = ({ cell }) => {
  if (!cell) return null;
  const s = { fontSize: 10.5, color: 'var(--text-4)', flexShrink: 0, whiteSpace: 'nowrap' };
  const warn = { ...s, color: 'var(--amber-fg)' };
  if (cell.differs && cell.source === 'computed') return <span style={warn} title={cell.formula}>≠ board</span>;
  if (cell.differs) return <span style={warn} title="The buyer board's amount differs from the letter">≠ letter</span>;
  if (cell.source === 'edited') return <span style={s} title={`Edited by ${cell.by || 'someone'}`}>edited</span>;
  if (cell.source === 'computed') return <span style={s} title={cell.formula}>=</span>;
  if (cell.source === 'roster') return <span style={s} title="From the buyer board">board</span>;
  if (cell.source === 'dealcloud') return <span style={s} title="DealCloud's LOI date on the buyer's pursuit">DC</span>;
  if (cell.page) return <span style={s} title={`Page ${cell.page} of the letter`}>p. {cell.page}</span>;
  if (cell.source === 'document') return <span style={s} title="From the letter (its text has no page numbers)">letter</span>;
  return null;
};

const LoiColumnHead = ({ col, canWrite, onPick, onReread, onTrail }) => {
  const r = col.review;
  const st = r && r.status;
  const muted = { fontSize: 11, color: 'var(--text-3)' };
  return (
    <th scope="col" style={{ borderBottom: '1px solid var(--border)', padding: '8px 10px', textAlign: 'left', verticalAlign: 'top',
                             width: LOI_COL_W, minWidth: LOI_COL_W, background: 'var(--surface)' }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        title={col.buyer_name}>{col.buyer_name}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap', fontWeight: 400 }}>
        {st === LOI_READING && <span style={muted}>Reading…</span>}
        {st === 'failed' && <span style={{ ...muted, color: 'var(--rose-fg)' }} title={r.error || ''}>Couldn't read</span>}
        {r && r.file && st !== LOI_READING && (
          <button type="button" onClick={() => window.AtlasApi.openAttachment(r.file.id, r.file.name)} title={r.file.name}
            style={{ ...muted, background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 11, cursor: 'pointer',
                     display: 'inline-flex', alignItems: 'center', gap: 3, maxWidth: 110 }}>
            <window.Icon.Doc size={10} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.file.name}</span>
          </button>
        )}
        {(!r || !r.file) && <span style={muted}>No letter</span>}
      </div>
      <div style={{ display: 'flex', gap: 2, marginTop: 4 }}>
        {canWrite && <Btn size="xs" variant="ghost" onClick={() => onPick(col)} disabled={st === LOI_READING}>
          {r && r.file ? 'Change letter' : 'Add letter'}</Btn>}
        {canWrite && r && r.file && st !== LOI_READING && (
          <Btn size="xs" variant="ghost" title="Read the letter again" icon={<Icon.Refresh size={11} />} onClick={() => onReread(col)} />
        )}
        {r && r.rules_changed && st !== LOI_READING && (
          <span style={{ ...muted, color: 'var(--amber-fg)', alignSelf: 'center' }}
            title="The red-flag list changed since this letter was read. Read it again to check it against the new list.">Rules changed</span>
        )}
        {r && (st === 'done' || st === 'failed') && (
          <Btn size="xs" variant="ghost" title="How it was read" icon={<Icon.Sparkle size={11} />} onClick={onTrail} />
        )}
      </div>
    </th>
  );
};

// ── the evidence pane: where a value came from, and editing it ──────────────
const LoiQuote = ({ quote, page, file }) => (
  <figure style={{ margin: 0 }}>
    <blockquote style={{ margin: 0, padding: '8px 10px', borderLeft: '3px solid var(--border-strong)', background: 'var(--surface-2)',
                         fontSize: 12.5, lineHeight: 1.5, color: 'var(--text)', overflowWrap: 'anywhere' }}>
      “{quote}”
    </blockquote>
    <figcaption style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {page ? <span>Page {page}</span> : null}
      {file && (
        <button type="button" onClick={() => window.AtlasApi.openAttachment(file.id, file.name)}
          style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--accent-text)', cursor: 'pointer' }}>
          Open letter
        </button>
      )}
    </figcaption>
  </figure>
);

// The letter's quoted clauses for one buyer. The table never carries them;
// they're fetched when a buyer's evidence opens (the server logs the read)
// and again only when the letter is read again.
const useLoiEvidence = (dealId, col) => {
  const r = col.review;
  const be = col.buyer_engagement_id;
  const stamp = r && r.file ? `${r.file.id}|${r.read_at || ''}|${r.status}` : '';
  const [ev, setEv] = React.useState(null);
  React.useEffect(() => {
    if (!stamp) { setEv(null); return undefined; }
    let alive = true;
    setEv(null);
    window.AtlasApi.loiEvidence(dealId, be).then((x) => { if (alive) setEv(x); })
      .catch(() => { if (alive) setEv({ error: true }); });
    return () => { alive = false; };
  }, [dealId, be, stamp]);
  return ev;
};

// The clause the letter states a term in — the same whether or not the cell was edited since.
const loiDocQuote = (ev, key) => {
  const c = ev && ev.cells && ev.cells[key];
  if (!c) return null;
  return c.source === 'document' ? c.quote : (c.doc && c.doc.quote) || null;
};

const LoiQuoteLoading = ({ ev }) => (ev && ev.error
  ? <div style={{ color: 'var(--text-3)' }}>Couldn't load the clause.</div>
  : <window.SkeletonRows rows={2} />);

const LoiEvidence = ({ deal, col, sel, terms, canWrite, onClose, onSaved }) => {
  const r = col.review || {};
  const trail = r.trail || {};
  const ev = useLoiEvidence(deal.id, col);
  const evOk = !!(ev && !ev.error);
  const term = sel.key ? terms.find((t) => t.key === sel.key) : null;
  const cell = term ? col.cells[term.key] : null;
  const flag = sel.flag != null ? col.red_flags[sel.flag] : null;
  const dq = term && evOk ? loiDocQuote(ev, term.key) : null;
  const flagQuote = flag && evOk ? ((ev.red_flags || []).find((f) => f.rule === flag.rule && f.page === flag.page) || {}).quote : null;
  const evTrail = evOk && ev.review ? ev.review.trail || {} : {};
  const [editing, setEditing] = React.useState(false);
  React.useEffect(() => { setEditing(false); }, [sel.be, sel.key, sel.flag]);
  const h = { fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '14px 0 6px' };
  const dropped = (trail.dropped || []).filter((x) => (term ? x.term === term.key : flag ? x.flag === flag.rule : true));
  const heading = term ? term.label : flag ? flag.label : 'How it was read';
  return (
    <div style={{ padding: '12px 16px 18px', fontSize: 12.5, color: 'var(--text)' }} aria-live="polite">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: flag ? 'var(--rose-fg)' : 'var(--text)' }}>{heading}</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{col.buyer_name}</div>
        </div>
        <Btn size="xs" variant="ghost" title="Close" icon={<Icon.Close size={12} />} onClick={onClose} />
      </div>

      {term && !editing && (
        <>
          <div className={term.kind === 'money' ? 'num' : undefined} style={{ fontSize: 18, fontWeight: 600, marginTop: 10 }}>
            {loiCellText(term, cell) || <span style={{ color: 'var(--text-4)', fontWeight: 400, fontSize: 13 }}>Blank</span>}
          </div>
          {cell && cell.text && term.kind === 'money' && <div style={{ color: 'var(--text-2)', marginTop: 4 }}>{cell.text}</div>}
          {cell && cell.source === 'computed' && <div style={{ color: 'var(--text-2)', marginTop: 6 }} className="num">= {cell.formula}</div>}
          {cell && cell.source === 'roster' && <div style={{ color: 'var(--text-3)', marginTop: 6 }}>From the buyer board</div>}
          {cell && cell.source === 'dealcloud' && <div style={{ color: 'var(--text-3)', marginTop: 6 }}>From DealCloud, on the buyer's pursuit</div>}
          {cell && cell.source === 'edited' && (
            <div style={{ color: 'var(--text-3)', marginTop: 6 }}>Edited by {cell.by || 'someone'}{cell.at ? ` · ${loiDate(cell.at)}` : ''}</div>
          )}
          {cell && cell.formula && cell.source === 'document' && <div style={{ color: 'var(--text-3)', marginTop: 6 }} className="num">= {cell.formula}</div>}
          {cell && (cell.source === 'document' || cell.doc) && (
            <>
              <div style={h}>
                {cell.source === 'document' ? 'In the letter'
                  : `The letter says${cell.doc.value != null ? ` ${loiMoney(cell.doc.value)}` : ''}`}
              </div>
              {dq ? <LoiQuote quote={dq} page={(cell.source === 'document' ? cell : cell.doc).page} file={r.file} />
                : <LoiQuoteLoading ev={ev} />}
            </>
          )}
          {!cell && r.status === 'done' && <div style={{ color: 'var(--text-3)', marginTop: 6 }}>The letter doesn't say.</div>}
          {!cell && !r.file && <div style={{ color: 'var(--text-3)', marginTop: 6 }}>No letter read for this buyer.</div>}
          {dropped.length > 0 && (
            <>
              <div style={h}>Not kept</div>
              {dropped.map((x, i) => <div key={i} style={{ color: 'var(--text-2)', marginBottom: 4 }}>{x.why}</div>)}
            </>
          )}
          {canWrite && term.editable && (
            <div style={{ display: 'flex', gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
              <Btn size="sm" variant="outline" icon={<Icon.Edit size={12} />} onClick={() => setEditing(true)}>Edit</Btn>
              {cell && (cell.source === 'edited' || cell.source === 'roster') && (
                <LoiClear deal={deal} col={col} term={term} cell={cell} onSaved={onSaved} />
              )}
            </div>
          )}
        </>
      )}
      {term && editing && <LoiEditForm deal={deal} col={col} term={term} cell={cell} onDone={() => setEditing(false)} onSaved={onSaved} />}

      {flag && (
        <>
          {flag.why && <div style={{ color: 'var(--text-2)', marginTop: 10 }}>{flag.why}</div>}
          {dropped.length > 0 && (
            <div style={{ color: 'var(--text-3)', marginTop: 6 }}>Not kept: {dropped.map((x) => x.why).join('; ')}</div>
          )}
          <div style={h}>In the letter</div>
          {flagQuote ? <LoiQuote quote={flagQuote} page={flag.page} file={r.file} /> : <LoiQuoteLoading ev={ev} />}
        </>
      )}

      {sel.trail && (
        <>
          {r.status === 'failed' && <div style={{ color: 'var(--rose-fg)', marginTop: 10 }}>{r.error}</div>}
          {trail.reasoning && <div style={{ color: 'var(--text-2)', marginTop: 10, lineHeight: 1.5 }}>{trail.reasoning}</div>}
          {trail.from_party && (
            <>
              <div style={h}>From</div>
              {evTrail.from_party ? <LoiQuote quote={evTrail.from_party.quote} file={null} /> : <LoiQuoteLoading ev={ev} />}
            </>
          )}
          <div style={h}>How it got here · {2 + ((trail.dropped || []).length ? 1 : 0)} steps</div>
          <ol style={{ margin: 0, paddingLeft: 18, color: 'var(--text-2)', lineHeight: 1.6 }}>
            <li>Read {trail.file || (r.file && r.file.name) || 'the letter'}{trail.pages ? ` · ${trail.pages} pages` : ''}{trail.read_as === 'parsed_text' ? ' · no page numbers' : ''}</li>
            <li>Checked every quote against the letter{trail.rules ? ` · ${trail.rules.length} red-flag terms` : ''}</li>
            {(trail.dropped || []).length > 0 && <li>Dropped {(trail.dropped || []).length}: {(trail.dropped || []).map((x) => `${x.term || x.flag} (${x.why})`).join('; ')}</li>}
          </ol>
          {r.read_at && (
            <div style={{ fontSize: 11.5, color: 'var(--text-4)', marginTop: 10 }}>
              {[r.read_by, loiDate(r.read_at), r.model].filter(Boolean).join(' · ')}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const LoiClear = ({ deal, col, term, cell, onSaved }) => {
  const [busy, setBusy] = React.useState(false);
  const label = cell.source === 'edited' && cell.doc ? 'Use the letter' : 'Clear';
  const go = () => {
    setBusy(true);
    window.AtlasApi.editLoiTerm(deal.id, col.buyer_engagement_id, { key: term.key, clear: true })
      .then(() => { if (cell.source === 'roster' && window.notifyBuyersChanged) window.notifyBuyersChanged(deal.id, 'loi-compare'); return onSaved(); })
      .catch((e) => window.pushToast({ type: 'error', title: "Couldn't clear it", body: e.message }))
      .finally(() => setBusy(false));
  };
  return <Btn size="sm" variant="ghost" onClick={go} disabled={busy}>{label}</Btn>;
};

const LoiEditForm = ({ deal, col, term, cell, onDone, onSaved }) => {
  const money = term.kind === 'money';
  const [value, setValue] = React.useState(cell && cell.value != null ? cell.value : null);
  const [txt, setTxt] = React.useState((cell && cell.text) || '');
  const earnout = term.key === 'earnout';
  const [basis, setBasis] = React.useState((cell && cell.basis) || 'unclear');
  const [period, setPeriod] = React.useState((cell && cell.period) || '');
  const [busy, setBusy] = React.useState(false);
  const save = (e) => {
    if (e) e.preventDefault();
    setBusy(true);
    const body = { key: term.key, value: money ? value : null, text: txt || null, ...(earnout ? { basis, period: period || null } : null) };
    window.AtlasApi.editLoiTerm(deal.id, col.buyer_engagement_id, body)
      .then(() => {
        if (window.notifyBuyersChanged && (term.key === 'headline_price' || term.key === 'date_received')) window.notifyBuyersChanged(deal.id, 'loi-compare');
        return onSaved();
      })
      .then(onDone)
      .catch((err) => window.pushToast({ type: 'error', title: "Couldn't save", body: err.message }))
      .finally(() => setBusy(false));
  };
  const field = { display: 'block', fontSize: 11.5, color: 'var(--text-3)', margin: '10px 0 4px' };
  return (
    <form onSubmit={save} style={{ marginTop: 6 }}>
      {money && (<label style={field}>Amount<window.MoneyInput value={value} onChange={setValue} placeholder="e.g. 19.5M" autoFocus /></label>)}
      {term.kind === 'date' && (<label style={field}>Date<window.TextInput type="date" value={txt} onChange={(e) => setTxt(e.target.value)} autoFocus /></label>)}
      {term.kind !== 'date' && term.key !== 'headline_price' && (
        <label style={field}>{money ? 'Note' : 'Term'}
          <window.TextInput value={txt} onChange={(e) => setTxt(e.target.value)} maxLength={300} autoFocus={!money}
            placeholder={money ? 'e.g. on EBITDA over $4M' : 'e.g. 60 days'} />
        </label>
      )}
      {earnout && (
        <>
          <label style={field}>Period
            <window.TextInput value={period} onChange={(e) => setPeriod(e.target.value)} maxLength={120} placeholder="e.g. 3 years" />
          </label>
          <div style={field}>In the headline?</div>
          <window.Picker value={basis} onChange={setBasis} size="sm" minWidth={200} title="Whether the headline price counts the earn-out"
            options={[{ value: 'included', label: 'Included in the headline' }, { value: 'additional', label: 'On top of the headline' },
                      { value: 'unclear', label: 'Unclear' }]} />
        </>
      )}
      <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
        <Btn size="sm" variant="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Btn>
        <Btn size="sm" variant="ghost" onClick={onDone} disabled={busy}>Cancel</Btn>
      </div>
    </form>
  );
};

// ── picking a buyer's letter: a file already on the deal, or an upload ─────
const LoiLetterPicker = ({ deal, col, onClose, onPicked }) => {
  const [files, setFiles] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [showAll, setShowAll] = React.useState(false);
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    let alive = true;
    window.AtlasApi.loiLetters(deal.id).then((r) => { if (alive) setFiles(r.files || []); })
      .catch((e) => { if (alive) setErr(e.message || 'Could not load files'); });
    return () => { alive = false; };
  }, [deal.id]);
  const be = col.buyer_engagement_id;
  const ordered = (files || []).slice().sort((a, b) => (b.suggested_for === be) - (a.suggested_for === be));
  const shown = showAll ? ordered : ordered.filter((f) => f.loi || f.suggested_for === be);
  const pick = (f) => {
    setBusy(true);
    window.AtlasApi.pickLoiLetter(deal.id, be, f.id).then(onPicked)
      .catch((e) => { setBusy(false); window.pushToast({ type: 'error', title: "Couldn't use that file", body: e.message }); });
  };
  const upload = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setBusy(true);
    window.AtlasApi.uploadLoiLetter(deal.id, be, f).then(onPicked)
      .catch((x) => { setBusy(false); window.pushToast({ type: 'error', title: 'Upload failed', body: x.message }); });
  };
  const row = { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 16px', background: 'none', border: 'none',
                borderBottom: '1px solid var(--border)', font: 'inherit', textAlign: 'left', cursor: busy ? 'wait' : 'pointer', color: 'var(--text)' };
  return (
    <window.Drawer open zIndex={60} width={480} widthKey="loiLetter" title="Pick the LOI" subtitle={col.buyer_name} onClose={onClose}
      footer={(
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input ref={inputRef} type="file" accept=".pdf,.docx,.doc,.txt,.rtf" onChange={upload} style={{ display: 'none' }} />
          <Btn size="sm" variant="outline" icon={<Icon.Upload size={12} />} onClick={() => inputRef.current && inputRef.current.click()} disabled={busy}>
            Upload letter
          </Btn>
          {busy && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Starting…</span>}
        </div>
      )}>
      {err && <div style={{ padding: 16, fontSize: 12.5, color: 'var(--rose-fg)' }}>{err}</div>}
      {!files && !err && <div style={{ padding: 16 }}><window.SkeletonRows rows={4} /></div>}
      {files && !shown.length && (
        <div style={{ padding: '16px', fontSize: 12.5, color: 'var(--text-3)' }}>
          {files.length ? 'No file on this deal looks like an LOI.' : 'No PDF or Word files on this deal.'}
        </div>
      )}
      {shown.map((f) => (
        <button key={f.id} type="button" style={row} onClick={() => !busy && pick(f)} disabled={busy}>
          <window.Icon.Doc size={13} color="var(--text-3)" />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
            <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {[f.via === 'vault' ? 'Data vault' : (f.note_title || 'Note'), loiDate(f.at)].filter(Boolean).join(' · ')}
            </span>
          </span>
          {f.suggested_for === be && <Pill hue="teal" sz="sm">Likely</Pill>}
        </button>
      ))}
      {files && !showAll && ordered.length > shown.length && (
        <div style={{ padding: '10px 16px' }}>
          <Btn size="xs" variant="ghost" onClick={() => setShowAll(true)}>All files on the deal · {ordered.length}</Btn>
        </div>
      )}
    </window.Drawer>
  );
};

// ── Settings → LOI red flags ─────────────────────────────────────────────────
const LoiRedFlagsSettings = () => {
  const [items, setItems] = React.useState(null);
  const [defaults, setDefaults] = React.useState([]);
  const [saved, setSaved] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    window.AtlasApi.loiRules().then((r) => { setItems(r.items); setSaved(JSON.stringify(r.items)); setDefaults(r.defaults || []); })
      .catch((e) => setErr(e.message || 'Could not load'));
  }, []);
  const dirty = items && JSON.stringify(items) !== saved;
  const set = (i, patch) => setItems((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const save = () => {
    setBusy(true);
    window.AtlasApi.saveLoiRules(items)
      .then((r) => { setItems(r.items); setSaved(JSON.stringify(r.items)); window.pushToast({ type: 'success', title: 'Saved' }); })
      .catch((e) => window.pushToast({ type: 'error', title: "Couldn't save", body: e.message }))
      .finally(() => setBusy(false));
  };
  if (err) return <div style={{ padding: '20px 28px', fontSize: 13, color: 'var(--rose-fg)' }}>{err}</div>;
  if (!items) return <div style={{ padding: '20px 28px' }}><window.SkeletonRows rows={3} /></div>;
  const lbl = { display: 'block', fontSize: 11.5, color: 'var(--text-3)', marginBottom: 4 };
  return (
    <div style={{ padding: '20px 28px', maxWidth: 760 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((it, i) => (
          <div key={it.key || i} style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', padding: 12,
                                          opacity: it.on ? 1 : 0.65 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <label style={{ flex: '1 1 260px', minWidth: 0 }}><span style={lbl}>Term</span>
                <window.TextInput value={it.label} maxLength={80} autoFocus={!it.key && !it.label} onChange={(e) => set(i, { label: e.target.value })} />
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-2)', height: 30 }}>
                <input type="checkbox" checked={!!it.on} onChange={(e) => set(i, { on: e.target.checked })} /> Flag it
              </label>
              <Btn size="sm" variant="ghost" title="Remove" icon={<Icon.Trash size={12} />} onClick={() => setItems((xs) => xs.filter((_, j) => j !== i))} />
            </div>
            <label style={{ display: 'block', marginTop: 8 }}><span style={lbl}>What counts</span>
              <textarea value={it.look_for || ''} maxLength={400} rows={2} onChange={(e) => set(i, { look_for: e.target.value })}
                style={{ width: '100%', padding: '6px 8px', fontSize: 12.5, border: '1px solid var(--border)', borderRadius: 5, background: 'var(--surface)',
                         color: 'var(--text)', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
            </label>
          </div>
        ))}
        {!items.length && <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>No terms — letters are read without red flags.</div>}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
        <Btn size="sm" variant="outline" icon={<Icon.Plus size={12} />} onClick={() => setItems((xs) => [...xs, { key: '', label: '', look_for: '', on: true }])}
          disabled={items.length >= 40}>Add term</Btn>
        <Btn size="sm" variant="ghost" onClick={() => setItems(defaults.map((x) => ({ ...x })))}>Reset to the huddle's two</Btn>
        <span style={{ marginLeft: 'auto' }} />
        <Btn size="sm" variant="primary" onClick={save} disabled={!dirty || busy || items.some((x) => !String(x.label || '').trim())}>
          {busy ? 'Saving…' : 'Save'}
        </Btn>
      </div>
    </div>
  );
};

Object.assign(window, { LoiCompareCard, LoiRedFlagsSettings });
